import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, ShoppingCart, TrendingUp, Package, CreditCard, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesAnalytics() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user || !user.is_admin) {
        navigate('/');
      } else {
        fetchAnalytics();
      }
    }
  }, [user, authLoading, navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/sales`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load sales analytics');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const { overview, category_sales, top_products, recent_sales, payment_status, payment_methods } = analytics || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12" data-testid="sales-analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="sales-analytics-title">Sales Analytics</h1>
          <p className="text-gray-600 mt-2">Comprehensive sales insights and performance metrics</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6 border-0 shadow-md" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Total Revenue</p>
                <p className="text-xl sm:text-3xl font-bold text-[#10B981]">${overview?.total_revenue || 0}</p>
              </div>
              <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-[#10B981]" />
            </div>
          </Card>

          <Card className="p-4 sm:p-6 border-0 shadow-md" style={{ background: 'rgba(0, 217, 255, 0.15)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Total Orders</p>
                <p className="text-xl sm:text-3xl font-bold text-[#00D9FF]">{overview?.total_orders || 0}</p>
              </div>
              <ShoppingCart className="h-6 w-6 sm:h-8 sm:w-8 text-[#00D9FF]" />
            </div>
          </Card>

          <Card className="p-4 sm:p-6 border-0 shadow-md" style={{ background: 'rgba(255, 107, 53, 0.15)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Avg Order Value</p>
                <p className="text-xl sm:text-3xl font-bold text-[#FF6B35]">${overview?.avg_order_value || 0}</p>
              </div>
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-[#FF6B35]" />
            </div>
          </Card>

          <Card className="p-4 sm:p-6 border-0 shadow-md" style={{ background: 'rgba(255, 184, 0, 0.15)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Pending Orders</p>
                <p className="text-xl sm:text-3xl font-bold text-[#FFB800]">{overview?.pending_orders || 0}</p>
              </div>
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-[#FFB800]" />
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Category Sales */}
          <Card className="p-4 sm:p-6 border-0 shadow-md">
            <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Sales by Category</h2>
            <div className="space-y-4">
              {category_sales && Object.entries(category_sales).map(([category, data]) => (
                <div key={category}>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm sm:text-base font-medium">{category}</span>
                    <span className="text-sm sm:text-base text-gray-600">${data.revenue.toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-[#FF6B35] h-2 rounded-full" 
                      style={{ width: `${Math.min((data.revenue / overview?.total_revenue) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{data.quantity} items sold</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Payment Methods */}
          <Card className="p-4 sm:p-6 border-0 shadow-md">
            <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Revenue by Payment Method</h2>
            <div className="space-y-4">
              {payment_methods && Object.entries(payment_methods).map(([method, revenue]) => (
                <div key={method} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#00D9FF]/10 rounded-full">
                      <CreditCard className="h-5 w-5 text-[#00D9FF]" />
                    </div>
                    <div>
                      <p className="font-medium capitalize text-sm sm:text-base">{method}</p>
                      <p className="text-xs text-gray-500">
                        {payment_status?.paid || 0} transactions
                      </p>
                    </div>
                  </div>
                  <span className="text-base sm:text-lg font-bold text-[#10B981]">${revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold mb-3">Payment Status</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-[#10B981]">{payment_status?.paid || 0}</p>
                  <p className="text-xs text-gray-600">Paid</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-[#FFB800]">{payment_status?.pending || 0}</p>
                  <p className="text-xs text-gray-600">Pending</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-gray-400">{payment_status?.failed || 0}</p>
                  <p className="text-xs text-gray-600">Failed</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Top Selling Products */}
        <Card className="p-4 sm:p-6 border-0 shadow-md mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Top Selling Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {top_products && top_products.map((product, idx) => (
              <div key={product.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <img 
                    src={product.image_url} 
                    alt={product.name} 
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{product.name}</p>
                  <p className="text-xs text-gray-500">{product.quantity_sold} sold</p>
                  <p className="text-sm font-bold text-[#10B981] mt-1">${product.revenue.toFixed(2)}</p>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center text-white font-bold text-sm">
                    #{idx + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Sales */}
        <Card className="p-4 sm:p-6 border-0 shadow-md">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Recent Sales</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 text-xs sm:text-sm font-semibold text-gray-600">Order ID</th>
                  <th className="text-left py-3 px-2 text-xs sm:text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left py-3 px-2 text-xs sm:text-sm font-semibold text-gray-600">Items</th>
                  <th className="text-left py-3 px-2 text-xs sm:text-sm font-semibold text-gray-600">Total</th>
                  <th className="text-left py-3 px-2 text-xs sm:text-sm font-semibold text-gray-600">Method</th>
                </tr>
              </thead>
              <tbody>
                {recent_sales && recent_sales.map((sale) => (
                  <tr key={sale.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2 text-xs sm:text-sm">
                      <span className="font-mono text-xs">{sale.id.substring(0, 8)}</span>
                    </td>
                    <td className="py-3 px-2 text-xs sm:text-sm">{sale.email}</td>
                    <td className="py-3 px-2 text-xs sm:text-sm">{sale.items?.length || 0}</td>
                    <td className="py-3 px-2 text-xs sm:text-sm font-bold text-[#10B981]">${sale.total.toFixed(2)}</td>
                    <td className="py-3 px-2 text-xs sm:text-sm capitalize">{sale.payment_method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
